import { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { useOnboardingPolling } from '@/onboarding/useOnboardingPolling'
import { bookmarkStep } from '@/api/onboarding'

const BOT_DISPLAY_NAME = 'TeleGraphyx бот продвижения'

export function ConnectSubscriberPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const locState = location.state as { maxDeepLink?: string } | null
  const maxDeepLink = locState?.maxDeepLink || `https://max.ru/?startapp=onboard_${token}`
  const [copied, setCopied] = useState(false)
  const { status, error } = useOnboardingPolling(token || null)

  useEffect(() => {
    if (status?.state === 'bot_subscribed' || status?.state === 'bot_admin' || status?.state === 'rights_partial' || status?.state === 'rights_ok') {
      bookmarkStep('connect-admin')
      navigate(`/onboarding/connect-admin/${encodeURIComponent(token!)}`, { replace: true, state: { maxDeepLink } })
    }
    if (status?.state === 'expired') {
      const t = setTimeout(() => navigate('/onboarding/source', { replace: true }), 3000)
      return () => clearTimeout(t)
    }
  }, [status?.state, token, navigate, maxDeepLink])

  function handleCopy() {
    navigator.clipboard.writeText(BOT_DISPLAY_NAME).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <OnboardingShell
      step="connect-subscriber"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate('/onboarding/source')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <span className="foot-meta">
            <span className="foot-pip" />
            Ждём появления бота в канале…
          </span>
        </>
      }
    >
      <p className="stage-eyebrow">
        <span className="stage-eyebrow-pill">Подключение · продолжение</span>
      </p>
      <h1 className="stage-title">
        Добавьте бота <em>в&nbsp;подписчики</em> канала
      </h1>
      <p className="stage-sub">
        Это первый из&nbsp;двух шагов. После того, как бот окажется в&nbsp;канале, мы&nbsp;сами поймём это и&nbsp;покажем, что делать дальше.
      </p>

      <div className="step-card">
        <ul className="step-list">
          <li className="step-row">
            <div className="step-num">1</div>
            <div className="step-content">
              <div className="step-text">Откройте настройки канала&nbsp;→ «Подписчики»</div>
              <div className="step-sub">Шапка канала&nbsp;→ шестерёнка&nbsp;→ раздел <b>«Подписчики»</b>&nbsp;→ «Добавить подписчиков».</div>
            </div>
          </li>
          <li className="step-row">
            <div className="step-num">2</div>
            <div className="step-content">
              <div className="step-text">
                Найдите по&nbsp;названию <b>«{BOT_DISPLAY_NAME}»</b>
              </div>
              <div className="step-sub">
                В&nbsp;MAX поиск работает только по&nbsp;названию&nbsp;— скопируйте название и&nbsp;вставьте в&nbsp;поиск, бот появится первым в&nbsp;списке.
              </div>
            </div>
            <button className="step-action" onClick={handleCopy}>
              {copied ? (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  Скопировано
                </>
              ) : (
                <>
                  Копировать
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="11" height="11" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </>
              )}
            </button>
          </li>
        </ul>

        <div className="check-status-row">
          <span className="pip" />
          <div>
            <b>Слушаем подключение</b>&nbsp;— как только бот появится в&nbsp;подписчиках, мы автоматически перейдём к&nbsp;следующему шагу. Обновлять страницу не&nbsp;нужно.
          </div>
        </div>
      </div>

      {error && <div className="onb-error-inline">Не удалось проверить статус: {error}</div>}

      <style>{`
        .step-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          overflow: hidden;
          max-width: 760px;
        }
        .step-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .step-row {
          display: grid;
          grid-template-columns: 36px 1fr auto;
          gap: 18px;
          padding: 22px 28px;
          align-items: flex-start;
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .step-num {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--tg-blue-bg, #ebf2ff);
          color: var(--tg-blue-press, #0066cc);
          font-weight: 800;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .step-text {
          font-weight: 600;
          font-size: 15px;
          color: var(--tg-ink, #111);
          margin-bottom: 4px;
        }
        .step-text b { font-weight: 700; }
        .step-sub {
          font-size: 12.5px;
          color: var(--tg-mute, #8f9eb0);
          line-height: 1.45;
        }
        .step-sub b { color: var(--tg-ink-2, #333); font-weight: 600; }
        .step-action {
          padding: 8px 14px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-3, #d0d0d8);
          border-radius: 8px;
          font-family: ui-monospace, SFMono-Regular, monospace;
          font-size: 12px;
          font-weight: 700;
          color: var(--tg-ink, #111);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .step-action:hover {
          border-color: var(--tg-blue, #0080ff);
          color: var(--tg-blue-press, #0066cc);
          background: var(--tg-blue-bg, #ebf2ff);
        }
        .step-action svg { width: 12px; height: 12px; }
        .check-status-row {
          padding: 18px 28px;
          background: var(--tg-blue-bg, #ebf2ff);
          display: flex;
          align-items: center;
          gap: 14px;
          font-size: 13.5px;
          color: var(--tg-ink-2, #333);
        }
        .check-status-row b { color: var(--tg-ink, #111); font-weight: 700; }
        .check-status-row .pip {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--tg-blue, #0080ff);
          animation: tg-pulse-pip 1.6s ease-out infinite;
          flex-shrink: 0;
        }
        @keyframes tg-pulse-pip {
          0% { box-shadow: 0 0 0 0 rgba(0,128,255,0.4); }
          100% { box-shadow: 0 0 0 8px rgba(0,128,255,0); }
        }
        .foot-meta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 600;
          color: var(--tg-mute, #8f9eb0);
          padding: 0 14px;
        }
        .foot-pip {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--tg-blue, #0080ff);
          animation: tg-pulse-pip 1.6s ease-out infinite;
        }
        .onb-error-inline {
          margin-top: 12px;
          padding: 10px 14px;
          background: rgba(255, 64, 0, 0.08);
          border-radius: 8px;
          color: #c33;
          font-size: 12px;
        }
        @media (max-width: 720px) {
          .step-row {
            grid-template-columns: 36px minmax(0, 1fr);
            gap: 14px;
            padding: 18px 16px;
          }
          .step-content { min-width: 0; }
          .step-text, .step-sub { overflow-wrap: anywhere; }
          .step-action {
            grid-column: 2;
            justify-self: start;
            margin-top: 8px;
          }
          .check-status-row {
            padding: 16px;
            align-items: flex-start;
          }
          .foot-meta { line-height: 1.4; }
        }
      `}</style>
    </OnboardingShell>
  )
}
